/**
 * Der `MassenuploadDialog` als nachgeladener Chunk: er (samt `lib/massenupload/`, den Fallansichten und `massenupload.css`)
 * gehört nicht ins Eintrittsbündel (Grenze und NEGATIVLISTE in `test/reference/bundle.test.ts`), erst das Ablegen von Dateien
 * lädt ihn. Wer den Dialog nur bei Bedarf rendert (`{auftrag && <MassenuploadDialogLazy … />}`), zahlt den Abruf erst dann.
 * Verwendet von `App.tsx`; Muster ist `components/Drawer/TrennenDialogLazy.tsx`.
 *
 * Diese Hülle importiert den Dialog nur zur Laufzeit und seine Props nur als Typ (`import type` wird gelöscht): ein statischer
 * Import des Dialogs zöge ihn samt `lib/massenupload/` ins Eintrittsbündel.
 *
 * ================= Wenn der Chunk nicht lädt =================
 *
 * Ein alter Tab nach einem Deployment kennt `MassenuploadDialog-<hash>.js` nicht mehr: `import()` scheitert, `React.lazy` wirft
 * beim Rendern. Ohne Grenze dazwischen fängt das erst die Wurzelgrenze (`FehlerGrenze` in `src/App.tsx`) und ersetzt die GANZE
 * Anwendung durch „Bitte die Seite neu laden“. Der Massenupload ist ein Nebenweg des Reiters Dokumente; Board und Drawer sollen
 * stehen bleiben (ungespeicherte Eingaben in der Sitzung gehen sonst verloren). `NachladeGrenze` (`components/NachladeGrenze.tsx`,
 * dieselbe Klasse wie bei den Einzelheiten der Artwork-Prüfung und den Zollinstruktionen) meldet den Fehler im Stapel
 * (bei einem Chunk-Ladefehler mit `FEHLERTEXTE.neuLaden`, das macht der Übersetzer `fehlertext`), ruft `onSchliessen` und zeichnet
 * nichts. `React.lazy` merkt sich die Ablehnung: ein zweiter Versuch ohne Neuladen scheitert gleich wieder, die Meldung bleibt dann
 * dieselbe.
 */
import { lazy, Suspense } from "react";
import { meldeFehler } from "../../../../state/fehlerStapel.ts";
import { NachladeGrenze } from "../../../NachladeGrenze.tsx";
import { EscapeSperre } from "../../TrennenDialogLazy.tsx";
import type { MassenuploadDialogProps } from "./MassenuploadDialog.tsx";

const MassenuploadDialogChunk = lazy(() => import("./MassenuploadDialog.tsx"));

// Neutral: die Grenze fängt auch einen Absturz mitten im Lauf, nicht nur den Chunk-Ladefehler.
const FEHLER_KONTEXT = "Der Massenupload wurde abgebrochen.";

/**
 * Bis der Chunk da ist, verschluckt `EscapeSperre` die Escape-Taste: der Dialog hat seinen eigenen Hörer noch nicht, und ein
 * Escape in dieser Phase würde sonst die Board-Auswahl leeren.
 */
export function MassenuploadDialogLazy(props: MassenuploadDialogProps) {
  return (
    <NachladeGrenze melde={meldeFehler} kontext={FEHLER_KONTEXT} onSchliessen={props.onSchliessen}>
      <Suspense fallback={<EscapeSperre />}>
        <MassenuploadDialogChunk {...props} />
      </Suspense>
    </NachladeGrenze>
  );
}
